import React, { useState, useRef, useEffect } from 'react'
import { Send, MessageCircle, X, Bot, User, Minimize2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import logomobile from '../assets/logo/logomobile.png'

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showIntroBubble, setShowIntroBubble] = useState(true)
  const [input, setInput] = useState('')

  const messagesEndRef = useRef(null)
  const chatRef = useRef(null)
  const inputRef = useRef(null)
  const introTimeoutRef = useRef(null)

  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: 'Hi! 👋 Welcome to S Infinite Express. How can I assist you today?'
    }
  ])

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    })
  }, [messages])

  // Welcome bubble
  useEffect(() => {
    if (isOpen) {
      setShowIntroBubble(false)
      clearTimeout(introTimeoutRef.current)
      return
    }

    const interval = setInterval(() => {
      setShowIntroBubble(true)

      clearTimeout(introTimeoutRef.current)

      introTimeoutRef.current = setTimeout(() => {
        setShowIntroBubble(false)
      }, 3000)
    }, 10000)

    return () => {
      clearInterval(interval)
      clearTimeout(introTimeoutRef.current)
    }
  }, [isOpen])

  // Auto responses
  const getAutoResponse = userInput => {
    const msg = userInput.toLowerCase()

    if (
      msg.includes('track') ||
      msg.includes('shipment') ||
      msg.includes('status')
    ) {
      return {
        text: (
          <>
            🔍 To track your shipment, please visit our{' '}
            <Link
              to='/track-shipment'
              className='font-semibold text-[#07529A] underline'
            >
              Tracking Page
            </Link>
            .
          </>
        )
      }
    }

    if (
      msg.includes('quote') ||
      msg.includes('price') ||
      msg.includes('cost')
    ) {
      return {
        text: (
          <>
            💰 Get a quote on our{' '}
            <Link
              to='/get-a-quote'
              className='font-semibold text-[#07529A] underline'
            >
              Quote Page
            </Link>{' '}
            or email{' '}
            <a
              href='mailto:info@sinfiniteexpress.com'
              className='font-semibold text-[#07529A] underline'
            >
              info@sinfiniteexpress.com
            </a>
            .
          </>
        )
      }
    }

    if (
      msg.includes('job') ||
      msg.includes('career') ||
      msg.includes('apply')
    ) {
      return {
        text: (
          <>
            📄 View current jobs on our{' '}
            <Link
              to='/join-us/career'
              className='font-semibold text-[#07529A] underline'
            >
              Careers Page
            </Link>
            .
          </>
        )
      }
    }

    if (
      msg.includes('services') ||
      msg.includes('offer') ||
      msg.includes('logistics')
    ) {
      return {
        text: (
          <>
            🚚 We offer PTL, FTL, Express Delivery, Warehousing, and more. Visit
            our{' '}
            <Link
              to='/services'
              className='font-semibold text-[#07529A] underline'
            >
              Services Page
            </Link>
            .
          </>
        )
      }
    }

    if (
      msg.includes('contact') ||
      msg.includes('support') ||
      msg.includes('help')
    ) {
      return {
        text: (
          <>
            📞 Call us at{' '}
            <a
              href='tel:+918237323531'
              className='font-semibold text-[#07529A] underline'
            >
              +91 82373 23531
            </a>{' '}
            or email{' '}
            <a
              href='mailto:support@sinfiniteexpress.com'
              className='font-semibold text-[#07529A] underline'
            >
              support@sinfiniteexpress.com
            </a>
            .
          </>
        )
      }
    }

    return {
      text: '🤖 Thanks for your message! How can I assist you further?',
      quickActions: true
    }
  }

  // Send message
  const handleSend = () => {
    if (!input.trim()) return

    const userMessage = input.trim()

    setMessages(prev => [
      ...prev,
      {
        sender: 'user',
        text: userMessage
      }
    ])

    setInput('')

    setTimeout(() => {
      const botReply = getAutoResponse(userMessage)

      setMessages(prev => [
        ...prev,
        {
          sender: 'bot',
          ...botReply
        }
      ])
    }, 800)
  }

  // Quick actions
  const handleQuickAction = label => {
    setMessages(prev => [
      ...prev,
      {
        sender: 'user',
        text: label
      }
    ])

    setTimeout(() => {
      const botReply = getAutoResponse(label)

      setMessages(prev => [
        ...prev,
        {
          sender: 'bot',
          ...botReply
        }
      ])
    }, 600)
  }

  // Enter key
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (chatRef.current && !chatRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 200)
    }
  }, [isOpen])

  const quickActions = [
    'Track Shipment',
    'Get Quote',
    'Careers',
    'Services',
    'Support'
  ]

  return (
    <>
      {/* Animation Styles */}
      <style>{`
    @keyframes chatSlideUp {
      from {
        opacity: 0;
        transform: translateY(25px) scale(0.96);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes chatFade {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes chatPulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 244, 79, 0.5);
      }
      50% {
        box-shadow: 0 0 0 12px rgba(255, 244, 79, 0);
      }
    }

    .chat-slide-up {
      animation: chatSlideUp 0.3s ease-out;
    }

    .chat-fade {
      animation: chatFade 0.3s ease-out;
    }

    .chat-pulse {
      animation: chatPulse 2s infinite;
    }

    .chat-scrollbar::-webkit-scrollbar {
      width: 5px;
    }

    .chat-scrollbar::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 10px;
    }

    @media (prefers-reduced-motion: reduce) {
      .chat-slide-up,
      .chat-fade,
      .chat-pulse {
        animation: none !important;
      }
    }
  `}</style>

      {/* Welcome Bubble */}
      {showIntroBubble && !isOpen && (
        <div className='fixed bottom-[150px] right-4 sm:bottom-[180px] sm:right-7 z-[9998] chat-fade'>
          <div className='relative bg-white text-[#211f57] text-xs sm:text-sm font-semibold shadow-xl px-4 sm:px-5 py-3 rounded-2xl border border-gray-100 whitespace-nowrap'>
            👋 Can I help you?
            <span className='absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 rotate-45' />
          </div>
        </div>
      )}
      {/* {showIntroBubble && !isOpen && (
        <div className='fixed bottom-[95px] right-[85px] sm:bottom-[110px] sm:right-[105px] z-[9998] chat-fade'>
          <div className='relative bg-white text-[#211f57] text-xs sm:text-sm font-semibold shadow-xl px-4 sm:px-5 py-3 rounded-2xl border border-gray-100 whitespace-nowrap'>
            👋 Can I help you?
            <span className='absolute top-1/2 -right-2 -translate-y-1/2 w-4 h-4 bg-white border-r border-t border-gray-100 rotate-45' />
          </div>
        </div>
      )} */}

      {/* Floating Chat Button */}
      <button
        type='button'
        onClick={() => {
          setIsOpen(prev => !prev)
          setShowIntroBubble(false)
        }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
        className='fixed bottom-[85px] right-4 sm:bottom-[110px] sm:right-7 z-[9999] w-[52px] h-[52px] sm:w-[64px] sm:h-[64px] rounded-full bg-[#FFF44F] text-[#211f57] shadow-[0_8px_30px_rgba(33,31,87,0.25)] flex items-center justify-center hover:scale-110 hover:bg-yellow-300 active:scale-95 transition-all duration-300 chat-pulse'
      >
        {isOpen ? (
          <X size={25} />
        ) : (
          <>
            <MessageCircle size={25} strokeWidth={2.4} />
            {/* <span className='absolute top-1 right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full' /> */}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          ref={chatRef}
          className='fixed z-[9999] bottom-[150px] right-3 left-3 sm:left-auto sm:right-7 sm:bottom-[185px] w-auto sm:w-[370px] max-w-[calc(100vw-24px)] h-[min(500px,calc(100dvh-180px))] sm:h-[540px] min-h-[320px] bg-white rounded-2xl sm:rounded-[24px] shadow-[0_15px_60px_rgba(0,0,0,0.20)] border border-gray-200 overflow-hidden flex flex-col chat-slide-up'
        >
          {/* Header */}
          <div className='relative bg-[#211f57] text-white px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between shadow-md shrink-0'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-[#FFF44F] shrink-0'>
                <img
                  src={logomobile}
                  alt='S Infinite Express'
                  className='w-full h-full object-contain'
                />
              </div>

              <div>
                <h3 className='font-bold text-sm sm:text-base leading-tight'>
                  S Infinite Assistant
                </h3>

                <div className='flex items-center gap-1.5 mt-1'>
                  <span className='w-2 h-2 rounded-full bg-green-400' />
                  <span className='text-[11px] text-gray-200'>
                    Online | Here to help
                  </span>
                </div>
              </div>
            </div>

            <button
              type='button'
              onClick={() => setIsOpen(false)}
              aria-label='Close chat'
              className='w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/15 transition shrink-0'
            >
              <Minimize2 size={19} />
            </button>
          </div>

          {/* Welcome Banner */}
          <div className='px-3 sm:px-4 py-2.5 sm:py-3 bg-[#FFF44F]/20 border-b border-yellow-100 shrink-0'>
            <p className='text-xs text-[#211f57] font-medium'>
              Welcome to S Infinite Express!
            </p>
            <p className='text-[11px] text-gray-600 mt-1'>
              Ask us about shipments, services, quotes or support.
            </p>
          </div>

          {/* Messages */}
          <div className='flex-1 min-h-0 p-3 sm:p-4 overflow-y-auto space-y-4 bg-[#F8F9FC] chat-scrollbar'>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex items-end gap-2 ${
                  msg.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {msg.sender === 'bot' && (
                  <div className='w-7 h-7 shrink-0 rounded-full bg-[#211f57] text-white flex items-center justify-center'>
                    <Bot size={15} />
                  </div>
                )}

                <div
                  className={`px-3.5 py-3 rounded-2xl text-[13px] leading-6 max-w-[82%] break-words ${
                    msg.sender === 'user'
                      ? 'bg-[#FFF44F] text-[#211f57] rounded-br-sm shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-700 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {msg.text}

                  {msg.quickActions && (
                    <div className='mt-3 flex flex-wrap gap-2'>
                      {quickActions.map(label => (
                        <button
                          key={label}
                          type='button'
                          onClick={() => handleQuickAction(label)}
                          className='bg-[#FFF44F]/40 border border-[#E9D936] px-3 py-1.5 text-[11px] font-semibold rounded-full text-[#211f57] hover:bg-[#FFF44F] transition'
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {msg.sender === 'user' && (
                  <div className='w-7 h-7 shrink-0 rounded-full bg-gray-200 text-[#211f57] flex items-center justify-center'>
                    <User size={15} />
                  </div>
                )}
              </div>
            ))}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className='p-3 bg-white border-t border-gray-100 shrink-0'>
            <div className='flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1.5 focus-within:border-[#211f57] focus-within:ring-2 focus-within:ring-[#211f57]/10 transition'>
              <input
                ref={inputRef}
                type='text'
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Type your message...'
                className='flex-1 min-w-0 bg-transparent px-2 py-2 text-sm text-gray-700 outline-none placeholder:text-gray-400'
              />

              <button
                type='button'
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label='Send message'
                className='w-10 h-10 shrink-0 rounded-lg bg-[#FFF44F] text-[#211f57] flex items-center justify-center hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition'
              >
                <Send size={17} />
              </button>
            </div>

            <p className='text-center text-[10px] text-gray-400 mt-2'>
              S Infinite Express | Your Cargo, Our Commitment
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default ChatBot
